import type { CompanyDetail } from '@planuze/admin-contracts';
import { InlineAlert } from '@planuze/ui';
import { Link } from '@tanstack/react-router';
import { Crown } from 'lucide-react';
import { useCompaniesContent } from '../../content/sections/companies.js';
import { fmtDate, fmtDateTime } from '../../lib/format.js';

type Member = CompanyDetail['members'][number];

function Field({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  );
}

/* Contagem por papel a partir dos membros já carregados no DTO. Ordena por frequência desc,
   depois alfabético — leitura estável. */
function roleBreakdown(members: readonly Member[]): { role: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const member of members) {
    counts.set(member.role, (counts.get(member.role) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([role, count]) => ({ role, count }))
    .sort((a, b) => b.count - a.count || a.role.localeCompare(b.role));
}

export function CompanyOverviewTab({ company }: { readonly company: CompanyDetail }) {
  const copy = useCompaniesContent();
  const owner = company.members.find((member) => member.role === 'owner') ?? null;
  const breakdown = roleBreakdown(company.members);
  return (
    <div className="space-y-6">
      {company.deletionRequestedAt !== null && (
        <InlineAlert variant="warning" title={copy.overviewTab.deletionRequestedTitle}>
          {copy.overviewTab.deletionRequestedDescription(fmtDate(company.deletionRequestedAt))}
        </InlineAlert>
      )}

      {owner !== null && (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-4">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Crown className="size-4" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="text-muted-foreground text-xs">{copy.overviewTab.owner}</div>
            <div className="truncate font-medium text-foreground">{owner.email}</div>
            <div className="text-muted-foreground text-xs">
              {copy.overviewTab.ownerSince(fmtDate(owner.joinedAt))}
            </div>
          </div>
        </div>
      )}

      <dl className="grid grid-cols-2 gap-4 text-sm">
        {company.legalName !== null && (
          <Field label={copy.overviewTab.legalName} value={company.legalName} />
        )}
        {company.taxId !== null && <Field label={copy.overviewTab.taxId} value={company.taxId} />}
        <Field label={copy.common.members} value={String(company.memberCount)} />
        <Field label={copy.common.createdAt} value={fmtDate(company.createdAt)} />
        {company.publisherSlug !== null && (
          <div>
            <dt className="text-muted-foreground">{copy.overviewTab.publisher}</dt>
            <dd>
              <Link to="/publishers" className="font-mono text-primary hover:underline">
                @{company.publisherSlug}
              </Link>
            </dd>
          </div>
        )}
        {company.blockedAt !== null && (
          <Field label={copy.overviewTab.blockedAt} value={fmtDateTime(company.blockedAt)} />
        )}
        {company.blockedReason !== null && (
          <Field label={copy.overviewTab.blockedReason} value={company.blockedReason} />
        )}
      </dl>

      {breakdown.length > 0 && (
        <div className="space-y-2">
          <div className="text-muted-foreground text-sm">{copy.overviewTab.roles}</div>
          <ul className="flex flex-wrap gap-2">
            {breakdown.map(({ role, count }) => (
              <li
                key={role}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted px-2.5 py-1 text-sm"
              >
                <span className="text-foreground">{role}</span>
                <span className="tabular-nums text-muted-foreground">{count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
